// ============================================================================
// Efficacy correlation + insight generation (PRD §9, §14)
//
// Pure functions — consume a list of sessions for a batch and emit patient
// insights. Separated from UI and Supabase so they are unit-testable and
// can eventually move into an Edge Function for the predictive layer.
// ============================================================================

export type SessionRecord = {
  id: string;
  // Nullable since migration 0019 — Oil/Accessory sessions have no
  // temperature. Sessions with null vape_temp_c are filtered out by
  // bucketByTemperature before aggregation, so they don't skew insights.
  vape_temp_c: number | null;
  efficacy_rating: number | null;
  started_at: string;
};

export type TempBucketInsight = {
  temp: number;
  averageRating: number;
  samples: number;
};

/**
 * Group sessions by whole-degree temperature and compute mean efficacy
 * per bucket. Only buckets with ≥2 samples are returned — a single data
 * point shouldn't drive a "try X°C" prompt.
 */
export function bucketByTemperature(sessions: SessionRecord[]): TempBucketInsight[] {
  const map = new Map<number, { sum: number; count: number }>();
  for (const s of sessions) {
    if (s.efficacy_rating == null || s.vape_temp_c == null) continue;
    // Round to whole degrees so 182.5 and 183 land in the same bucket.
    const temp = Math.round(s.vape_temp_c);
    const existing = map.get(temp) ?? { sum: 0, count: 0 };
    existing.sum += s.efficacy_rating;
    existing.count += 1;
    map.set(temp, existing);
  }
  return [...map.entries()]
    .filter(([, v]) => v.count >= 2)
    .map(([temp, v]) => ({
      temp,
      averageRating: Number((v.sum / v.count).toFixed(2)),
      samples: v.count,
    }))
    .sort((a, b) => a.temp - b.temp);
}

/**
 * Generates the "you had 20% better relief at 195°C" prompt (PRD §14).
 * Returns null when there is insufficient signal.
 */
export function temperatureInsight(
  sessions: SessionRecord[],
): { bestTemp: number; bestRating: number; uplift: number } | null {
  const buckets = bucketByTemperature(sessions);
  if (buckets.length < 2) return null;

  const best = buckets.reduce((a, b) => (b.averageRating > a.averageRating ? b : a));
  const others = buckets.filter((b) => b.temp !== best.temp);
  if (others.length === 0) return null;

  const otherAvg =
    others.reduce((acc, b) => acc + b.averageRating, 0) / others.length;

  const uplift = otherAvg > 0 ? Math.round(((best.averageRating - otherAvg) / otherAvg) * 100) : 0;
  if (uplift < 15) return null; // Below the noise threshold.

  return { bestTemp: best.temp, bestRating: best.averageRating, uplift };
}
